import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { imgUrl, fmtGram, fmtDate, STATUS_COLORS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import GramSummary from "@/components/GramSummary";
import InvoicePanel from "@/components/InvoicePanel";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hideMilyem = user?.hide_milyem;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  if (!order) return <p className="text-center py-24 text-muted-foreground">Sipariş bulunamadı.</p>;

  return (
    <div data-testid="order-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-gold mb-4 transition-colors">
        <ArrowLeft size={16} /> Geri
      </button>

      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="font-display text-3xl text-foreground">{order.order_number}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs px-3 py-1.5 rounded border ${STATUS_COLORS[order.status] || ""}`}>{order.status}</span>
          <span className={`text-xs px-3 py-1.5 rounded border ${order.gram_finalized ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>{order.gram_finalized ? "Kesinleşen Gram" : "Tahmini Gram"}</span>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-1">{fmtDate(order.created_at)}</p>
      {order.est_delivery && <p className="text-sm text-gold mb-4" data-testid="est-delivery">Tahmini teslim / üretim süresi: <b>{order.est_delivery}</b></p>}

      <button
        data-testid="view-proforma-button"
        onClick={() => navigate(`/invoice/${order.id}`)}
        className="flex items-center gap-2 mb-6 px-4 h-10 border border-gold/40 text-gold rounded-md hover:bg-gold/10 transition-colors"
      >
        <FileText size={16} /> Proforma Faturayı Görüntüle / Yazdır
      </button>

      <div className="space-y-3 mb-6">
        {order.items.map((it, idx) => (
          <div key={idx} className="flex gap-3 bg-surface border border-border rounded-md p-3">
            <div className="w-16 h-20 rounded overflow-hidden bg-elevated shrink-0">
              {it.image_url && <img src={imgUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground line-clamp-1">{it.name}</p>
              <p className="font-mono text-[11px] text-muted-foreground">{it.code}</p>
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                <span>{it.karat} Ayar</span><span className="text-border">•</span>
                <span>~{fmtGram(it.gram)} gr/adet</span>
                {it.size && (<><span className="text-border">•</span><span className="text-gold">Boy {it.size}</span></>)}
                {!hideMilyem && (<><span className="text-border">•</span><span>İşçilik {it.milyem || 0} milyem</span></>)}
              </div>
              <div className="flex justify-between mt-1 text-sm">
                <span className="text-muted-foreground">{it.quantity} adet</span>
                <span className="text-gold font-semibold">{order.gram_finalized ? "" : "~"}{fmtGram(it.line_gram)} gr · has {fmtGram(it.line_has)} gr</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {order.note && (
        <div className="bg-surface border border-border rounded-md p-4 mb-4">
          <div className="text-xs text-muted-foreground mb-1">Sipariş Notu</div>
          <p className="text-sm text-foreground">{order.note}</p>
        </div>
      )}

      <div className="mb-4">
        <GramSummary breakdown={order.karat_breakdown} totalPureHas={order.total_pure_has} totalIscHas={order.total_isc_has} totalHas={order.total_has} estimated={!order.gram_finalized} />
      </div>

      <div className="mb-6">
        <InvoicePanel order={order} onUpdated={setOrder} />
      </div>

      {order.history?.length > 0 && (
        <div>
          <h2 className="text-sm font-medium text-foreground mb-3">Sipariş Geçmişi</h2>
          <div className="space-y-2">
            {order.history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-gold" />
                <span className="text-foreground">{h.status}</span>
                <span className="text-muted-foreground text-xs ml-auto">{fmtDate(h.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
